import React from 'react';
import { motion } from 'framer-motion';
import { Camera, Film, Users, Sparkles } from 'lucide-react';
import { services } from '../data/mockData';
import { useLang } from '../context/LanguageContext';

const iconMap = { Camera, Film, Users, Sparkles };

const t = {
  fr: {
    label: 'Nos Services',
    h2a: 'Des expériences digitales',
    h2b: 'qui marquent les esprits',
    sub: 'Nous ne créons pas du contenu. Nous racontons des histoires et construisons des émotions.',
    cta: 'Discutons de votre projet',
  },
  en: {
    label: 'Our Services',
    h2a: 'Digital experiences',
    h2b: 'that leave a lasting impression',
    sub: 'We don\'t just create content. We tell stories and build emotions.',
    cta: 'Let\'s discuss your project',
  },
};

const ServicesSection = () => {
  const { lang } = useLang();
  const tx = t[lang];
  const servicesList = services[lang];

  return (
    <section id="services" className="relative py-24 md:py-32 bg-[#151515]">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-5xl mx-auto mb-16 text-center">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-6">
            <span className="text-sm uppercase tracking-widest text-[#717171] font-mono">{tx.label}</span>
          </motion.div>
          <motion.h2 initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.2 }} className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 leading-tight">
            {tx.h2a}<br /><span className="text-[#d4a574]">{tx.h2b}</span>
          </motion.h2>
          <motion.p initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.4 }} className="text-lg md:text-xl text-white/60 max-w-3xl mx-auto">
            {tx.sub}
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {servicesList.map((service, index) => {
            const IconComponent = iconMap[service.icon];
            return (
              <motion.div key={service.id} initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-50px" }} transition={{ duration: 0.7, delay: index * 0.15, ease: "easeOut" }} whileHover={{ y: -10, transition: { duration: 0.3 } }} className="group relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border border-white/10 hover:border-[#d4a574]/30 transition-all duration-500">
                <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-500">
                  <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
                </div>
                <div className="relative p-8 md:p-10 space-y-4">
                  <div className="w-16 h-16 rounded-full bg-[#d4a574]/10 flex items-center justify-center group-hover:bg-[#d4a574]/20 transition-colors duration-300">
                    <IconComponent className="text-[#d4a574]" size={32} />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-semibold text-white group-hover:text-[#d4a574] transition-colors duration-300">{service.title}</h3>
                  <p className="text-white/70 text-base md:text-lg leading-relaxed">{service.description}</p>
                  <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-[#d4a574] via-[#4a5d6c] to-[#1a3a52] transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6, delay: 0.6 }} className="text-center mt-16">
          <button onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })} className="cta-button bg-[#d4a574] text-white hover:bg-[#e8c9a0] transition-all px-8 py-4">
            {tx.cta}
          </button>
        </motion.div>
      </div>
      <div className="absolute top-1/2 left-0 w-96 h-96 bg-[#4a5d6c]/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#1a3a52]/5 rounded-full blur-3xl pointer-events-none" />
    </section>
  );
};

export default ServicesSection;
